/**
 * ECharts option for a resolved chart block. Colours come from chartTheme().
 */
import { cellText } from './shape.js';

/**
 * @param {unknown} label
 * @returns {string}
 */
export function labelText(label) {
  if (label && typeof label === 'object') return cellText(label.text != null ? label : { text: label.label });
  return label == null ? '' : String(label);
}

/**
 * @param {unknown} label
 * @returns {string}
 */
export function labelRaw(label) {
  if (!label || typeof label !== 'object' || label.raw == null) return '';
  return String(label.raw);
}

/**
 * @param {object} spec
 * @returns {number[]}
 */
export function chartNumbers(spec) {
  const nums = [];
  for (const series of spec.series || []) {
    for (const item of series.data || []) {
      const value = item && typeof item === 'object' ? Number(item.value) : Number(item);
      if (Number.isFinite(value)) nums.push(value);
    }
  }
  return nums;
}

/**
 * Title, range, min and max for the chart's accessible name.
 * @param {object} spec
 * @returns {string}
 */
export function chartAriaLabel(spec) {
  const title = spec.title || 'Chart';
  const range = spec.subtitle ? `${spec.subtitle}. ` : '';
  const nums = chartNumbers(spec);
  if (!nums.length) return `${title}. ${range}`.trim();
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  return `${title}. ${range}Minimum ${min}, maximum ${max}.`.replace(/\s+/g, ' ').trim();
}

function seriesData(series) {
  return (series.data || []).map((item) => (item && typeof item === 'object' ? Number(item.value) || 0 : Number(item) || 0));
}

function escapeTip(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function axisLabel(format) {
  if (format === 'percent') return { formatter: '{value}%' };
  return {};
}

/**
 * @param {object} spec
 * @param {{ text: string, muted: string, border: string, surface: string, background: string, palette: string[] }} theme
 * @returns {object}
 */
export function chartBlockOption(spec, theme) {
  const labels = (spec.labels || []).map(labelText);
  const raws = (spec.labels || []).map(labelRaw);
  const palette = theme.palette && theme.palette.length ? theme.palette : ['#ff4d12'];
  const text = theme.text || '#121316';
  const muted = theme.muted || text;
  const axis = theme.border || '#2e3036';
  const tipBg = theme.surface || theme.background || '#121316';
  const kind = spec.chartType || 'line';
  const tooltip = {
    backgroundColor: tipBg,
    borderColor: axis,
    borderWidth: 1,
    textStyle: { color: text, fontSize: 12 },
  };
  if (kind === 'pie' || kind === 'doughnut') {
    const series = (spec.series && spec.series[0]) || { data: [] };
    const data = seriesData(series).map((value, index) => ({
      name: labels[index] || String(index + 1),
      value,
    }));
    return {
      animation: false,
      backgroundColor: 'transparent',
      color: palette,
      tooltip: { ...tooltip, trigger: 'item' },
      legend: { bottom: 0, type: 'scroll', textStyle: { color: muted, fontSize: 11 } },
      series: [{
        type: 'pie',
        radius: kind === 'doughnut' ? ['42%', '68%'] : '68%',
        data,
        label: { color: text, fontSize: 11 },
      }],
    };
  }
  const horizontal = kind === 'hbar';
  const category = {
    type: 'category',
    data: labels,
    axisLabel: { color: muted, fontSize: 11 },
    axisLine: { lineStyle: { color: axis } },
    axisTick: { show: false },
  };
  const valueAxis = {
    type: 'value',
    axisLabel: { color: muted, fontSize: 11, ...axisLabel(spec.yFormat) },
    splitLine: { lineStyle: { color: axis } },
  };
  const chartSeries = (spec.series || []).map((series, index) => {
    const color = palette[index % palette.length];
    const bar = kind === 'column' || kind === 'bar' || kind === 'hbar' || (kind === 'combo' && index === 0);
    return {
      name: series.name || `Series ${index + 1}`,
      type: bar ? 'bar' : 'line',
      data: seriesData(series),
      smooth: !bar,
      barMaxWidth: 28,
      itemStyle: { color },
      lineStyle: bar ? undefined : { width: 2, color },
      areaStyle: kind === 'area' && (spec.series || []).length === 1 ? { color, opacity: 0.16 } : undefined,
    };
  });
  return {
    animation: false,
    backgroundColor: 'transparent',
    color: palette,
    // Legend lives in an HTML row above the plot (chart.js) so it never
    // overlaps the x-axis labels at phone or desktop widths.
    grid: { left: 8, right: 16, top: 12, bottom: 8, containLabel: true },
    tooltip: {
      ...tooltip,
      trigger: 'axis',
      formatter(params) {
        const rows = Array.isArray(params) ? params : [params];
        const first = rows[0] || {};
        const raw = raws[first.dataIndex] || '';
        const head = raw ? `${first.name} (${raw})` : (first.name || '');
        const lines = rows.map((row) => `${row.seriesName}: ${row.value}`);
        return [head, ...lines].filter(Boolean).map(escapeTip).join('<br>');
      },
    },
    legend: { show: false },
    xAxis: horizontal ? valueAxis : category,
    yAxis: horizontal ? category : valueAxis,
    series: chartSeries,
  };
}
